import { Music2, Volume2, VolumeX } from "lucide-react"
import { useCallback, useRef, useState, type CSSProperties } from "react"
import { LandingSnake } from "@/components/LandingSnake"

type BaubleStyle = CSSProperties & Record<`--${string}`, string>

const baubles: Array<{
  src: string
  className: string
  style: BaubleStyle
}> = [
  {
    src: "/solo/icon-star.png",
    className:
      "left-[13%] top-[25%] h-8 w-8 sm:left-[9%] sm:top-[18%] sm:h-14 sm:w-14",
    style: {
      "--drift-x": "18px",
      "--drift-y": "-24px",
      "--rotate-start": "-18deg",
      "--rotate-end": "14deg",
      "--duration": "8s",
      "--delay": "-1.5s",
    },
  },
  {
    src: "/solo/icon-bolt.png",
    className:
      "right-[14%] top-[24%] h-9 w-9 sm:right-[10%] sm:top-[16%] sm:h-16 sm:w-16",
    style: {
      "--drift-x": "-18px",
      "--drift-y": "22px",
      "--rotate-start": "9deg",
      "--rotate-end": "-16deg",
      "--duration": "7s",
      "--delay": "-3s",
    },
  },
  {
    src: "/solo/icon-note.png",
    className:
      "left-[15%] bottom-[22%] h-9 w-9 sm:left-[12%] sm:bottom-[21%] sm:h-[72px] sm:w-[72px]",
    style: {
      "--drift-x": "24px",
      "--drift-y": "18px",
      "--rotate-start": "12deg",
      "--rotate-end": "-22deg",
      "--duration": "9s",
      "--delay": "-5s",
    },
  },
  {
    src: "/solo/icon-flame.png",
    className:
      "right-[16%] bottom-[22%] h-9 w-9 sm:right-[14%] sm:bottom-[19%] sm:h-[70px] sm:w-[70px]",
    style: {
      "--drift-x": "-22px",
      "--drift-y": "-20px",
      "--rotate-start": "-8deg",
      "--rotate-end": "20deg",
      "--duration": "8.5s",
      "--delay": "-2s",
    },
  },
]

function LandingAudioControl() {
  const audioRef = useRef<HTMLAudioElement>(null)
  const [status, setStatus] = useState<"idle" | "blocked" | "loading" | "playing">(
    "idle",
  )
  const isPlaying = status === "playing"

  const playAudio = useCallback(async () => {
    const audio = audioRef.current
    if (!audio) {
      return
    }

    audio.volume = 0.22
    setStatus("loading")

    try {
      await audio.play()
      setStatus("playing")
    } catch {
      setStatus("blocked")
    }
  }, [])

  const toggleAudio = () => {
    const audio = audioRef.current

    if (isPlaying) {
      audio?.pause()
      window.localStorage.setItem("wildGuessLandingAudio", "off")
      setStatus("idle")
      return
    }

    window.localStorage.setItem("wildGuessLandingAudio", "on")
    void playAudio()
  }

  return (
    <div className="fixed right-4 top-[calc(env(safe-area-inset-top)+1rem)] z-50 sm:right-6 sm:top-6">
      <audio
        ref={audioRef}
        src="/audio/motel-pop-quiz.mp3"
        loop
        preload="none"
        onLoadedMetadata={(event) => {
          event.currentTarget.volume = 0.22
        }}
        onPlay={() => setStatus("playing")}
        onPause={() =>
          setStatus((currentStatus) =>
            currentStatus === "blocked" || currentStatus === "loading"
              ? currentStatus
              : "idle",
          )
        }
      />
      <button
        type="button"
        aria-label={isPlaying ? "Pause background music" : "Play background music"}
        aria-pressed={isPlaying}
        className={`group flex h-11 max-w-[11.5rem] touch-manipulation items-center gap-2 rounded-full px-3 text-left text-white backdrop-blur-md transition hover:border-[#c8e840]/55 hover:bg-[#171749]/78 active:scale-[0.98] sm:h-12 sm:max-w-none sm:px-4 ${
          isPlaying
            ? "border border-[#c8e840]/44 bg-[#171749]/86 shadow-[0_18px_54px_-34px_rgba(0,0,0,0.9),0_0_30px_-16px_rgba(200,232,64,0.9)]"
            : "border border-white/14 bg-[#11113a]/72 shadow-[0_18px_54px_-34px_rgba(0,0,0,0.9)]"
        }`}
        onClick={toggleAudio}
      >
        <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-white/10 text-[#c8e840] shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]">
          {isPlaying ? (
            <Volume2 aria-hidden="true" size={16} strokeWidth={2.8} />
          ) : status === "blocked" ? (
            <Music2 aria-hidden="true" size={16} strokeWidth={2.8} />
          ) : (
            <VolumeX aria-hidden="true" size={16} strokeWidth={2.8} />
          )}
        </span>
        <span className="hidden min-w-0 sm:block">
          <span className="block text-[0.56rem] font-extrabold uppercase tracking-[0.18em] text-white/46">
            Teaser mix
          </span>
          <span className="block truncate text-[0.68rem] font-extrabold uppercase tracking-[0.14em] text-[#f8f7ff]">
            {isPlaying
              ? "Motel Pop Quiz"
              : status === "loading"
                ? "Starting..."
                : "Tap for sound"}
          </span>
        </span>
        <span
          aria-hidden="true"
          className={`audio-meter ml-0.5 flex h-5 w-5 shrink-0 items-end justify-center gap-[2px] ${isPlaying ? "is-playing" : ""}`}
        >
          <span />
          <span />
          <span />
        </span>
      </button>
    </div>
  )
}

export function TeaserLanding() {
  return (
    <main className="teaser-landing relative isolate min-h-[100dvh] overflow-hidden bg-[#09091f] px-5 text-[#f8f7ff] sm:px-8">
      <LandingAudioControl />
      <div aria-hidden="true" className="teaser-stage-lines" />
      <div aria-hidden="true" className="teaser-ribbon teaser-ribbon-lime" />
      <div aria-hidden="true" className="teaser-ribbon teaser-ribbon-sky" />
      <div aria-hidden="true" className="teaser-ribbon teaser-ribbon-pink" />

      <div
        aria-hidden="true"
        className="absolute left-1/2 top-1/2 h-[min(82vw,540px)] w-[min(82vw,540px)] -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/10"
      />
      <div
        aria-hidden="true"
        className="absolute left-1/2 top-1/2 h-[min(66vw,420px)] w-[min(66vw,420px)] -translate-x-1/2 -translate-y-1/2 rounded-full border border-dashed border-[#c0e040]/25"
      />

      {baubles.map((bauble) => (
        <img
          key={bauble.src}
          src={bauble.src}
          alt=""
          aria-hidden="true"
          className={`teaser-bauble pointer-events-none absolute object-contain opacity-95 ${bauble.className}`}
          style={bauble.style}
        />
      ))}

      <section className="relative mx-auto flex min-h-[100dvh] w-full max-w-[980px] items-center justify-center py-12 sm:py-16">
        <div className="teaser-logo-stage relative flex w-full max-w-[24rem] flex-col items-center text-center sm:max-w-[42rem]">
          <img
            src="/wild-guess-logo-transparent.png"
            alt="Wild Guess"
            width={1448}
            height={702}
            className="relative h-auto w-[min(68vw,15rem)] object-contain drop-shadow-[0_26px_42px_rgba(0,0,0,0.42)] sm:w-72"
          />
          <h1 className="relative mt-8 whitespace-nowrap font-display text-[clamp(2.85rem,13vw,6.75rem)] font-extrabold leading-[0.88] tracking-tight text-[#f8f7ff]">
            Coming soon.
          </h1>
          <LandingSnake />
        </div>
      </section>
      <footer className="relative mx-auto -mt-4 w-full max-w-[980px] px-4 pb-[calc(env(safe-area-inset-bottom)+1.5rem)] text-center sm:-mt-8 sm:pb-8">
        <p className="text-[0.62rem] font-extrabold uppercase tracking-[0.16em] text-white/34">
          Curiously Created in partnership with{" "}
          <a
            href="https://clubwestcreative.com"
            target="_blank"
            rel="noreferrer"
            className="text-white/52 underline decoration-white/18 underline-offset-4 transition hover:text-[#c8e840] hover:decoration-[#c8e840]/55"
          >
            Club West Creative
          </a>
        </p>
      </footer>
    </main>
  )
}
